import { useMemo } from 'react'

import type { UploadError } from './postSlice'
import { useComposerStore } from '../hooks/useComposerStore'

export const useSelectedTagIds = (): string[] => {
  return useComposerStore((state) => state.selectedTagIds)
}

/** The ContentItem id this composer session edits in place, if any. */
export const useOmniDraftId = (): string | null => {
  return useComposerStore((state) => state.omni.draftId)
}

/** `ContentItem.title`, if one is set on the channel-less draft being edited. */
export const useOmniTitle = (): string | null => {
  return useComposerStore((state) => state.omni.title)
}

export const useSetOmniTitle = (): ((title: string | null) => void) => {
  return useComposerStore((state) => state.setOmniTitle)
}

/**
 * True when the omni draft has authored content (non-empty text or any asset).
 * Used to gate the channel-less Save Draft affordance in the omnibox footer.
 */
export const useOmniHasContent = (): boolean => {
  return useComposerStore(
    (state) =>
      state.omni.text.trim().length > 0 || state.omni.assets.length > 0,
  )
}

export const useSetSelectedTagIds = (): ((tagIds: string[]) => void) => {
  return useComposerStore((state) => state.setSelectedTagIds)
}

export const useInitialPostNeedsApproval = (): boolean => {
  return useComposerStore((state) => !!state.initialPost?.needsApproval)
}

/**
 * The id of the saved Post this session edits in place, if any.
 *
 * Same id as the GraphQL `Post`: the edit path resolves it through
 * `getPost` from the id the caller passed to `editPostInComposer`. Absent
 * while creating, duplicating, or editing a channel-less draft — none of
 * those have a persisted Post yet.
 */
export const useEditedPostId = (): string | undefined => {
  return useComposerStore((state) => state.initialPost?.id)
}

export const useUploadErrorsForChannelService = (
  channelService: string,
): UploadError[] => {
  const uploadErrors = useComposerStore((state) => state.uploadErrors)
  return useMemo(
    () =>
      uploadErrors.filter(
        (error) => error.context.channelService === channelService,
      ),
    [uploadErrors, channelService],
  )
}

export const useDismissUploadError = (): ((errorId: string) => void) => {
  return useComposerStore((state) => state.dismissUploadError)
}

export const useClearUploadErrors = (): ((channelService?: string) => void) => {
  return useComposerStore((state) => state.clearUploadErrors)
}

export const useAddUploadError = (): ((
  error: Omit<UploadError, 'id' | 'timestamp'>,
) => void) => {
  return useComposerStore((state) => state.addUploadError)
}
